/**
 * Brand registry — single source of truth for every hostname this app serves.
 *
 * Each brand has a `mode`:
 *   - "primary"   → main authority site (OBC)
 *   - "microsite" → topic-focused, indexable on its own canonical host
 *   - "alias"     → defensive brand-capture domain; should 301 at registrar/CDN.
 *   - "preview"   → Lovable preview/staging; always noindex,nofollow,noarchive.
 *
 * `sitemapRoutes` is the explicit, review-gated indexable URL set for this host.
 * Each entry has `path`, `indexable`, `reviewed`. The sitemap edge function
 * emits ONLY entries that are both indexable AND reviewed.
 *
 * Admins can override `reviewed` at runtime via the `microsite_route_overrides`
 * table; the edge function joins it before emitting.
 */
import { COMPANY } from "@/lib/constants";

export type BrandKey =
  | "obc"
  | "rcf"
  | "frr"
  | "oru"
  | "orfl"
  | "fcr"
  | "ftr"
  | "fsr"
  | "fmr"
  | "ck";

export type BrandMode = "primary" | "microsite" | "alias" | "preview";

export interface SitemapRoute {
  path: string;
  indexable: boolean;
  reviewed: boolean;
}

export interface BrandProfile {
  brand: BrandKey;
  mode: BrandMode;
  hostnames: string[];
  canonicalHost: string;
  canonicalOrigin: string;
  siteName: string;
  tagline: string;
  defaultTitle: string;
  defaultDescription: string;
  ogImage: string;
  twitterHandle?: string;
  phone: string;
  phoneFormatted: string;
  phoneHref: string;
  isMicroSite: boolean;
  indexable: boolean;
  parentBrand: string;
  license: string;
  primaryTopic:
    | "general"
    | "certificate"
    | "replacement"
    | "commercial"
    | "tile"
    | "shingle"
    | "metal"
    | "coatings";
  attribution: string;
  sitemapRoutes: SitemapRoute[];
  /** Pillar hub link on the OBC parent site for cross-brand E-E-A-T linking. */
  obcPillarUrl?: string;
  obcPillarLabel?: string;
}

const sharedPhone = {
  phone: COMPANY.phone,
  phoneFormatted: COMPANY.phoneFormatted,
  phoneHref: COMPANY.phoneHref,
};

const reviewed = (path: string): SitemapRoute => ({ path, indexable: true, reviewed: true });
const draft = (path: string): SitemapRoute => ({ path, indexable: false, reviewed: false });

const OBC: BrandProfile = {
  brand: "obc",
  mode: "primary",
  hostnames: [
    "obriencontractingusa.com",
    "www.obriencontractingusa.com",
    "obriencontractusa.com",
    "www.obriencontractusa.com",
    "obriencontractusa.lovable.app",
  ],
  canonicalHost: "obriencontractingusa.com",
  canonicalOrigin: "https://obriencontractingusa.com",
  siteName: COMPANY.name,
  tagline: "South Florida's Trusted Roofing Contractor",
  defaultTitle: "O'Brien Contracting | South Florida's Trusted Roofing Contractor",
  defaultDescription: `Licensed Florida roofing contractor serving North Port, Venice, Sarasota, Fort Myers, Naples, and South Florida. Call ${COMPANY.phoneFormatted}.`,
  ogImage: "https://obriencontractingusa.com/og-image.jpg",
  twitterHandle: "@obriencontracting",
  ...sharedPhone,
  isMicroSite: false,
  indexable: true,
  parentBrand: COMPANY.name,
  license: COMPANY.license,
  primaryTopic: "general",
  attribution: `${COMPANY.name} • FL License ${COMPANY.license}`,
  sitemapRoutes: [], // OBC sitemap built dynamically from DB in edge function
};

const RCF: BrandProfile = {
  brand: "rcf",
  mode: "microsite",
  hostnames: ["roofcertificateflorida.com", "www.roofcertificateflorida.com"],
  canonicalHost: "www.roofcertificateflorida.com",
  canonicalOrigin: "https://www.roofcertificateflorida.com",
  siteName: "RoofCertificateFlorida.com",
  tagline: "Florida Roof Certificates for Insurance",
  defaultTitle: "Florida Roof Certificate for Insurance Renewal | RoofCertificateFlorida.com",
  defaultDescription:
    "Get a Florida Roof Certificate to keep your homeowner's insurance, satisfy underwriter requirements, and document roof condition. Licensed Florida inspectors statewide.",
  ogImage: "https://www.roofcertificateflorida.com/images/hero-roofing-1280.webp",
  ...sharedPhone,
  isMicroSite: false,
  indexable: true,
  parentBrand: COMPANY.name,
  license: COMPANY.license,
  primaryTopic: "certificate",
  attribution: `A specialty resource operated by ${COMPANY.name} • FL License ${COMPANY.license}`,
  sitemapRoutes: [
    reviewed("/"),
    reviewed("/about"),
    reviewed("/trust"),
    reviewed("/editorial-policy"),
    reviewed("/authors"),
  ],
  obcPillarUrl: "https://obriencontractingusa.com/guides/wind-mitigation",
  obcPillarLabel: "Wind Mitigation Guide on O'Brien Contracting",
};

interface MicroSiteOpts {
  brand: BrandKey;
  apex: string;
  mode: BrandMode;
  siteName: string;
  tagline: string;
  topic: BrandProfile["primaryTopic"];
  metaTitle: string;
  metaDesc: string;
  sitemapRoutes?: SitemapRoute[];
  obcPillarUrl?: string;
  obcPillarLabel?: string;
}

const buildBrand = ({
  brand, apex, mode, siteName, tagline, topic, metaTitle, metaDesc,
  sitemapRoutes = [], obcPillarUrl, obcPillarLabel,
}: MicroSiteOpts): BrandProfile => {
  const indexable = mode === "microsite";
  return {
    brand,
    mode,
    hostnames: [apex, `www.${apex}`],
    canonicalHost: `www.${apex}`,
    canonicalOrigin: `https://www.${apex}`,
    siteName,
    tagline,
    defaultTitle: metaTitle,
    defaultDescription: metaDesc,
    ogImage: "https://obriencontractingusa.com/og-image.jpg",
    ...sharedPhone,
    isMicroSite: mode === "microsite",
    indexable,
    parentBrand: COMPANY.name,
    license: COMPANY.license,
    primaryTopic: topic,
    attribution: `${siteName} is an ${COMPANY.name} specialty roofing resource. Service performed by ${COMPANY.name}, Florida License ${COMPANY.license}.`,
    sitemapRoutes: indexable ? sitemapRoutes : [],
    obcPillarUrl,
    obcPillarLabel,
  };
};

// Wave 1+2 indexable route plans. Anchor pages reviewed-on-ship.
// Service detail routes mirror existing microSiteContent slugs under /services/:slug.
const COMMERCIAL_ROUTES: SitemapRoute[] = [
  reviewed("/"),
  reviewed("/services"),
  reviewed("/about"),
  reviewed("/contact"),
  reviewed("/faq"),
  reviewed("/services/tpo-roofing"),
  reviewed("/services/pvc-roofing"),
  reviewed("/services/modified-bitumen-roofing"),
  reviewed("/services/commercial-metal-roofing"),
  draft("/services/silicone-roof-coatings"),
];

const METAL_ROUTES: SitemapRoute[] = [
  reviewed("/"),
  reviewed("/services"),
  reviewed("/about"),
  reviewed("/contact"),
  reviewed("/faq"),
  reviewed("/services/standing-seam-metal-roofing"),
  reviewed("/services/metal-roof-replacement"),
  reviewed("/services/metal-roof-repair"),
  draft("/services/metal-vs-shingle-roofing"),
];

const SHINGLE_ROUTES: SitemapRoute[] = [
  reviewed("/"),
  reviewed("/services"),
  reviewed("/about"),
  reviewed("/contact"),
  reviewed("/faq"),
  reviewed("/services/architectural-shingle-roofing"),
  reviewed("/services/impact-rated-shingle-roofing"),
  reviewed("/services/shingle-roof-replacement"),
  reviewed("/services/shingle-roof-repair"),
  draft("/services/shingle-vs-metal-roofing"),
];

export const BRAND_REGISTRY: BrandProfile[] = [
  OBC,
  RCF,
  buildBrand({
    brand: "fcr",
    apex: "florida-commercial-roofing.com",
    mode: "microsite",
    siteName: "Florida Commercial Roofing",
    tagline: "Commercial TPO, PVC, Modified Bitumen & Metal — Florida",
    topic: "commercial",
    metaTitle: "Florida Commercial Roofing | TPO, PVC, Modified Bitumen, Metal",
    metaDesc: `Florida commercial roofing contractor: TPO, PVC, modified bitumen, metal, coatings. Licensed (CCC1335947), bonded, insured. Call ${COMPANY.phoneFormatted}.`,
    sitemapRoutes: COMMERCIAL_ROUTES,
    obcPillarUrl: "https://obriencontractingusa.com/services/commercial-roofing",
    obcPillarLabel: "Commercial Roofing on O'Brien Contracting",
  }),
  buildBrand({
    brand: "fmr",
    apex: "florida-metal-roofing.com",
    mode: "microsite",
    siteName: "Florida Metal Roofing",
    tagline: "Standing Seam, 5V Crimp & Metal Roof Specialists — Florida",
    topic: "metal",
    metaTitle: "Florida Metal Roofing | Standing Seam & 5V Crimp Specialists",
    metaDesc: `Florida metal roofing — standing seam, 5V crimp, stone-coated steel. Hurricane-rated, lifetime materials, licensed FL contractor. Call ${COMPANY.phoneFormatted}.`,
    sitemapRoutes: METAL_ROUTES,
    obcPillarUrl: "https://obriencontractingusa.com/services/metal-roofing",
    obcPillarLabel: "Metal Roofing on O'Brien Contracting",
  }),
  buildBrand({
    brand: "fsr",
    apex: "florida-shingle-roofing.com",
    mode: "microsite",
    siteName: "Florida Shingle Roofing",
    tagline: "Architectural & Impact-Rated Shingle Specialists — Florida",
    topic: "shingle",
    metaTitle: "Florida Shingle Roofing | Architectural & Impact-Rated Installers",
    metaDesc: `Florida shingle roofing — architectural and impact-rated shingle installs and replacements. Licensed contractor, financing available. Call ${COMPANY.phoneFormatted}.`,
    sitemapRoutes: SHINGLE_ROUTES,
    obcPillarUrl: "https://obriencontractingusa.com/services/shingle-roofing",
    obcPillarLabel: "Shingle Roofing on O'Brien Contracting",
  }),

  // === Alias / defensive (NOT indexable; user handles 301 externally) ===
  buildBrand({ brand: "oru", apex: "obrienroofingusa.com", mode: "alias",
    siteName: "O'Brien Roofing USA", tagline: "Visit O'Brien Contracting", topic: "general",
    metaTitle: "O'Brien Roofing USA", metaDesc: "Visit O'Brien Contracting — Florida's licensed roofing contractor." }),
  buildBrand({ brand: "orfl", apex: "obrienroofingfl.com", mode: "alias",
    siteName: "O'Brien Roofing FL", tagline: "Visit O'Brien Contracting", topic: "general",
    metaTitle: "O'Brien Roofing FL", metaDesc: "Visit O'Brien Contracting — Florida's licensed roofing contractor." }),
  buildBrand({ brand: "frr", apex: "floridaroofreplacement.com", mode: "alias",
    siteName: "Florida Roof Replacement", tagline: "Visit O'Brien Contracting", topic: "replacement",
    metaTitle: "Florida Roof Replacement", metaDesc: "Visit O'Brien Contracting — Florida's licensed roofing contractor for full roof replacement." }),
  buildBrand({ brand: "ftr", apex: "florida-tile-roofing.com", mode: "alias",
    siteName: "Florida Tile Roofing", tagline: "Visit O'Brien Contracting", topic: "tile",
    metaTitle: "Florida Tile Roofing", metaDesc: "Visit O'Brien Contracting for concrete and clay tile roofing in Florida." }),
  buildBrand({ brand: "ck", apex: "coatingkingz.com", mode: "alias",
    siteName: "Coating Kingz", tagline: "Visit O'Brien Contracting", topic: "coatings",
    metaTitle: "Coating Kingz", metaDesc: "Visit O'Brien Contracting for roof coatings and restoration services." }),
];

const PREVIEW_HOST_PATTERNS = [/\.lovable\.app$/i, /^id-preview--/i];

export const isPreviewHost = (hostname: string): boolean => {
  const h = hostname.toLowerCase();
  return PREVIEW_HOST_PATTERNS.some((re) => re.test(h));
};

export const detectBrandProfile = (hostname?: string): BrandProfile => {
  const rawHost = (
    hostname ?? (typeof window !== "undefined" ? window.location.hostname : "")
  ).toLowerCase();
  const host = rawHost.replace(/^www\./, "");

  for (const brand of BRAND_REGISTRY) {
    if (brand.hostnames.some((h) => h.replace(/^www\./, "") === host)) {
      return brand;
    }
  }
  return OBC;
};

export const MICRO_SITE_HOSTNAMES = BRAND_REGISTRY
  .filter((b) => b.mode === "microsite" && b.brand !== "rcf")
  .flatMap((b) => b.hostnames);

export const ALIAS_HOSTNAMES = BRAND_REGISTRY
  .filter((b) => b.mode === "alias")
  .flatMap((b) => b.hostnames);

/**
 * Returns the registry's published+indexable opinion for a given route path
 * on a given brand. Admin overrides from the DB are applied separately
 * (only the sitemap edge function joins them).
 */
export const getRouteFlags = (
  brand: BrandProfile,
  path: string,
): SitemapRoute | null => {
  const normalized = path === "" ? "/" : path;
  return brand.sitemapRoutes.find((r) => r.path === normalized) ?? null;
};

/**
 * True if a microsite route should ship `noindex,nofollow`. A page is fully
 * indexable only if its registry entry exists with `indexable && reviewed`,
 * AND the brand itself is indexable. Unlisted routes default to noindex.
 */
export const isRouteNoindex = (brand: BrandProfile, path: string): boolean => {
  if (!brand.indexable) return true;
  // OBC has its own per-page Helmet defaults; we only gate microsite routes here.
  if (!brand.isMicroSite && brand.brand !== "rcf") return false;
  const flags = getRouteFlags(brand, path);
  if (!flags) return true;
  return !(flags.indexable && flags.reviewed);
};

export const BRAND_HEAD_BOOTSTRAP = BRAND_REGISTRY.map((b) => ({
  hosts: b.hostnames,
  mode: b.mode,
  indexable: b.indexable,
  title: b.defaultTitle,
  desc: b.defaultDescription,
  canonical: b.canonicalOrigin,
  ogImage: b.ogImage,
  siteName: b.siteName,
}));
